import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, FileText, IndianRupee, AlertTriangle, Calendar, Clock } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { getCustomers, getBills } from "@/lib/api";

export default function Dashboard() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [bills, setBills] = useState([]);

  useEffect(() => {
    getCustomers().then(setCustomers).catch(() => {});
    getBills().then(setBills).catch(() => {});
  }, []);

  const now = new Date();
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();

  const billsThisMonth = bills.filter((b) => {
    const d = new Date(b.billDate);
    return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
  });

  const collectionThisMonth = billsThisMonth.filter((b) => b.isPaid).reduce((s, b) => s + (b.netPayable || 0), 0);
  const totalDueThisMonth = billsThisMonth.reduce((s, b) => s + (b.netPayable || 0), 0);
  const pendingDues = bills.filter((b) => !b.isPaid).reduce((s, b) => s + (b.netPayable || 0), 0);
  const overdueBills = bills.filter((b) => !b.isPaid && new Date(b.dueDate) < now);
  const collectionPct = totalDueThisMonth > 0 ? Math.round((collectionThisMonth / totalDueThisMonth) * 100) : 0;

  const lastBillDate = bills.length > 0
    ? [...bills].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]?.billDate
    : null;

  const chartData = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(thisYear, thisMonth - 5 + i, 1);
    const m = d.getMonth();
    const y = d.getFullYear();
    return {
      name: d.toLocaleString("default", { month: "short" }),
      collection: bills.filter((b) => { const bd = new Date(b.billDate); return bd.getMonth() === m && bd.getFullYear() === y && b.isPaid; }).reduce((s, b) => s + (b.netPayable || 0), 0),
    };
  });

  const recentBills = [...bills].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 10);

  const fmt = (v) => v.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="dashboard">
      <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900 mb-8">Dashboard</h1>

      {/* Row 1: Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[
          { label: "Total Customers", value: customers.length, icon: Users, color: "text-blue-600 bg-blue-50" },
          { label: "Bills This Month", value: billsThisMonth.length, icon: FileText, color: "text-orange-600 bg-orange-50" },
          { label: "Collection This Month", value: fmt(collectionThisMonth), icon: IndianRupee, color: "text-emerald-600 bg-emerald-50" },
          { label: "Pending Dues", value: fmt(pendingDues), icon: AlertTriangle, color: "text-red-600 bg-red-50" },
        ].map((card) => (
          <Card key={card.label} data-testid={`card-${card.label.toLowerCase().replace(/\s+/g, "-")}`} className="border border-zinc-200 rounded-none md:rounded-sm shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{card.label}</span>
                <div className={`w-7 h-7 rounded flex items-center justify-center ${card.color}`}><card.icon className="w-3.5 h-3.5" /></div>
              </div>
              <p className="text-xl md:text-2xl font-heading font-black text-zinc-900">{card.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Row 2: Secondary Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none" data-testid="card-last-billing-date">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Last Billing Date</span>
              <div className="w-7 h-7 rounded flex items-center justify-center text-violet-600 bg-violet-50"><Calendar className="w-3.5 h-3.5" /></div>
            </div>
            <p className="text-lg font-heading font-black text-zinc-900">{lastBillDate ? new Date(lastBillDate).toLocaleDateString("en-IN") : "—"}</p>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none" data-testid="card-overdue-bills">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Overdue Bills</span>
              <div className="w-7 h-7 rounded flex items-center justify-center text-red-600 bg-red-50"><Clock className="w-3.5 h-3.5" /></div>
            </div>
            <p className="text-lg font-heading font-black text-red-600">{overdueBills.length}</p>
          </CardContent>
        </Card>
        <Card className="col-span-2 lg:col-span-1 border border-zinc-200 rounded-none md:rounded-sm shadow-none" data-testid="card-collection-progress">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">This Month: Collected vs Due</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-3 bg-zinc-200 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${collectionPct}%` }} />
              </div>
              <span className="text-sm font-heading font-black text-zinc-900">{collectionPct}%</span>
            </div>
            <p className="text-[10px] text-zinc-500 mt-1 font-mono">{fmt(collectionThisMonth)} / {fmt(totalDueThisMonth)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Chart + Recent Bills */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border border-zinc-200 rounded-none md:rounded-sm shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Last 6 Months Collection</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[260px]" data-testid="collection-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#71717a" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#71717a" }} />
                  <Tooltip contentStyle={{ background: "#18181b", border: "none", borderRadius: 0, color: "#fff", fontSize: 12 }} formatter={(v) => [`₹${v.toFixed(2)}`, "Collection"]} />
                  <Bar dataKey="collection" fill="#ea580c" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Recent Bills</CardTitle></CardHeader>
          <CardContent className="p-0">
            {recentBills.length === 0 ? (
              <p className="text-sm text-zinc-400 p-6">No bills generated yet.</p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {recentBills.map((bill) => (
                  <button key={bill.id} data-testid={`recent-bill-${bill.id}`} onClick={() => navigate(`/bills/invoice/${bill.id}`)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-zinc-50 transition-colors text-left">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{bill.customerName}</p>
                      <p className="text-xs text-zinc-500 font-mono">{bill.invoiceNo}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold font-mono text-zinc-900">₹{(bill.netPayable || 0).toFixed(2)}</p>
                      <Badge variant={bill.isPaid ? "default" : "destructive"} className="text-[10px] mt-0.5">{bill.isPaid ? "Paid" : "Unpaid"}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
